import { useEffect, useState, useMemo } from 'react'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import clsx from 'clsx'

import { Spin } from '@pepper/ui'

import { getPreDataJSON } from '@pepper/utils/src/api/utils/utils'

import useUserAccountType from 'utils/hooks/useUserAccountType'

import Modal from 'container/Projects/CreateProjectV2/components/modal'

import { ReactComponent as ArrowRight } from 'assets/images/tabArrow.svg'

import styles from './_.module.css'

type ProjectDuplicationServiceSelectionModalProps = {
  serviceIdsList: (number | number[])[]
  closeDuplicateConfirmationModal: () => void
}

const ProjectDuplicationServiceSelectionModal =
  NiceModal.create<ProjectDuplicationServiceSelectionModalProps>(
    ({ serviceIdsList, closeDuplicateConfirmationModal }) => {
      const modal = useModal()
      const accountType = useUserAccountType()

      const [servicesListJson, setServicesListJson] =
        useState<PreDataProps | null>(null)

      useEffect(() => {
        const fetchPreDataJson = async () => {
          if (typeof accountType !== 'string') return

          const isStandard = accountType === 'standard'

          const res = await getPreDataJSON({ isStandard })
          if (res.data) {
            setServicesListJson(res.data)
          }
        }
        fetchPreDataJson()
      }, [accountType])

      const allServicesFlatJson = useMemo(() => {
        if (!servicesListJson) return null
        const flatServicesListData: Record<
          string,
          TabComponentOptions['options'][number] & { categoryName: string }
        > = {}
        servicesListJson.typeContentSelection.body.options.forEach(
          categoryJson => {
            categoryJson.options.forEach(serviceJson => {
              flatServicesListData[serviceJson.id] = {
                ...serviceJson,
                categoryName: categoryJson.name,
              }
            })
          }
        )
        return flatServicesListData
      }, [servicesListJson])

      const serviceIdsListJson = useMemo(() => {
        if (!allServicesFlatJson) return []
        const _serviceIdsListJson: (
          | ((typeof allServicesFlatJson)[string] & { isGrouped: false })
          | ((typeof allServicesFlatJson)[string] & {
              isGrouped: true
              groupedServiceIdJsons: (typeof allServicesFlatJson)[string][]
            })
        )[] = []
        serviceIdsList.forEach(serviceIds => {
          if (Array.isArray(serviceIds)) {
            const serviceJson = allServicesFlatJson[serviceIds[0]]
            if (accountType === 'standard' && !serviceJson.isStandard) return

            _serviceIdsListJson.push({
              ...serviceJson,
              isGrouped: true,
              groupedServiceIdJsons: serviceIds
                .slice(1)
                .map(serviceId => allServicesFlatJson[serviceId])
                .filter(serviceJson =>
                  accountType === 'standard' ? serviceJson.isStandard : true
                ),
            })
            return
          }

          const serviceJson = allServicesFlatJson[serviceIds]
          // Skip serviceId only when standard account and serviceId not allowed in standard account
          if (accountType === 'standard' && !serviceJson?.isStandard) return
          _serviceIdsListJson.push({ ...serviceJson, isGrouped: false })
        })

        return _serviceIdsListJson
      }, [allServicesFlatJson, accountType, serviceIdsList])

      const handleSubmit = (serviceId: number | number[] | null) => {
        if (!serviceId) return
        modal.resolve(serviceId)
        return
      }

      if (!servicesListJson) {
        return (
          <div className={styles.pageLoader}>
            <Spin />
          </div>
        )
      }

      return (
        <div className={styles.createProjectWrap}>
          <Modal
            wrapClassName={styles.createProjectWrap}
            header="Select a service to duplicate"
            onCancel={() => {
              modal.remove()
              closeDuplicateConfirmationModal()
            }}
            visible={modal.visible}
            footer={null}
          >
            <div className={styles.alertWrap}>
              <span className={styles.alertHeading}>NOTE</span>
              <span className={styles.alertText}>
                Brief of the selected service will be duplicated from the
                previous project.
              </span>
            </div>
            <div className={styles.tabWrap}>
              <div className={styles.bodyWrap}>
                {serviceIdsListJson.map((serviceJson, index) => {
                  return serviceJson.isGrouped ? (
                    <div
                      key={index}
                      role="menuitem"
                      tabIndex={index}
                      onClick={() =>
                        handleSubmit([
                          serviceJson.id,
                          ...serviceJson.groupedServiceIdJsons.map(
                            groupedServiceJson => groupedServiceJson.id
                          ),
                        ])
                      }
                      onKeyPress={() =>
                        handleSubmit([
                          serviceJson.id,
                          ...serviceJson.groupedServiceIdJsons.map(
                            groupedServiceJson => groupedServiceJson.id
                          ),
                        ])
                      }
                      className={clsx(styles.tabRow)}
                    >
                      <img
                        className={styles.tabImage}
                        src={serviceJson?.icon_set?.default}
                        alt={`tab-${index}`}
                      />
                      <div className={styles.tabsMiddleRow}>
                        <span>
                          {`${serviceJson.title} + `}
                          {serviceJson.groupedServiceIdJsons
                            .map(groupedServiceJson => groupedServiceJson.title)
                            .join(' + ')}
                        </span>
                        <span className={styles.tabCategoryName}>
                          {`${serviceJson.categoryName} + `}
                          {serviceJson.groupedServiceIdJsons
                            .map(
                              groupedServiceJson =>
                                groupedServiceJson.categoryName
                            )
                            .join(' + ')}
                        </span>
                      </div>

                      <ArrowRight className={styles.tabArrow} />
                    </div>
                  ) : (
                    <div
                      key={index}
                      role="menuitem"
                      tabIndex={index}
                      onClick={() => handleSubmit(serviceJson.id)}
                      onKeyPress={() => handleSubmit(serviceJson.id)}
                      className={clsx(styles.tabRow)}
                    >
                      <img
                        className={styles.tabImage}
                        src={serviceJson?.icon_set?.default}
                        alt={`tab-${index}`}
                      />
                      <div className={styles.tabsMiddleRow}>
                        <span>{serviceJson.title}</span>
                        <span className={styles.tabCategoryName}>
                          {serviceJson.categoryName}
                        </span>
                      </div>
                      <ArrowRight className={styles.tabArrow} />
                    </div>
                  )
                })}
              </div>
            </div>
          </Modal>
        </div>
      )
    }
  )

export default ProjectDuplicationServiceSelectionModal
